<template>
  <HeightTransition>
    <div class="flex flex-col gap-md">
      <div class="flex flex-col gap-md">

        <div class="md:max-w-screen-sm">
          <PersonalMessageTemplateSelector
            @select="handleMessageTemplateSelect($event)"
          />
        </div>

        <FloatingLabelTextArea
          id="message"
          :value="value"
          class="mb-0"
          @input="$emit('input', $event)"
          @blur="$emit('blur', $event)"
          :has-error="!!errors && !!errors.length"
          :placeholder="$t('ui.envelopes.create.steps.parties.personalMessageTab.addMessageContentField.label')"
          input-classes="h-48"
          data-ec="envelope_create_add_recipients_personal_message_tab"
          data-et="message_textarea"
        />

        <ValidationError
          class="!mb-0"
          :hasError="!!errors && !!errors.length"
          :errorMessage="errors && errors[0]"
        >
          <template
            v-if="errors && errors[0] === 'MessageRequired'"
            v-slot:content
          >
            <p class="text-14 font-bold mb-0">
              {{ $t('ui.envelopes.create.steps.parties.personalMessageTab.required') }}
            </p>
            <p class="text-14">
              {{ $t('ui.envelopes.create.steps.parties.personalMessageTab.advice') }}
              <span
                class="underline cursor-pointer"
                @click="$emit('remove')"
              >
                {{
                  $t('ui.envelopes.create.steps.parties.personalMessageTab.adviceLink')
                }}
              </span>
            </p>
          </template>
        </ValidationError>

      </div>

      <div class="flex gap-md">
        <div
          v-if="!showSaveOptions"
          class="flex justify-start"
        >
          <BaseButton
            @click.native="showSaveOptions = true"
            variant="invisible"
            size="small"
            class="mr-none">
            {{ $t('ui.envelopes.create.steps.parties.personalMessageTab.saveMessageAsNewTemplateAction') }}
          </BaseButton>
        </div>

        <div
          v-if="showSaveOptions"
          class="flex w-full flex-col lg:flex-row justify-between items-start gap-md"
        >
          <div class="flex flex-col w-full md:max-w-screen-sm gap-md">
            <FloatingLabelInput
              v-model="templateNameText"
              class="mb-0"
              :placeholder="$t('ui.envelopes.create.steps.parties.personalMessageTab.addTemplateName.label')"
              data-ec="envelope_create_add_recipients_personal_message_tab"
              data-et="message_template_name_input"
            />
            <ValidationError
              :hasError="!!templateNameErrorMessage"
              :errorMessage="templateNameErrorMessage"
            />
          </div>
          <div class="flex flex-col sm:flex-row items-center gap-md">
            <BaseButton
              @click.native="handleSaveMessageTemplate"
              :disabled="!templateNameText || !value"
              variant="secondary"
              size="large"
              class="w-full md:w-auto text-center !whitespace-normal md:!whitespace-nowrap"
            >
              {{ $t('ui.envelopes.create.steps.parties.personalMessageTab.saveMessageTemplateAction') }}
            </BaseButton>
            <BaseButton
              @click.native="cancelTemplateSave"
              variant="invisible">
              {{ $t('functional.cancel') }}
            </BaseButton>
          </div>
        </div>
      </div>
    </div>
  </HeightTransition>
</template>

<script>
// @ts-check
import { computed, defineComponent, ref } from 'vue';

import ValidationError from '@/components/library/error/ValidationError/ValidationError.vue';
import BaseButton from '@/components/library/buttons/BaseButton.vue';
import FloatingLabelTextArea from '@/components/tailwind/forms/FloatingLabelTextarea.vue';
import HeightTransition from '@/transitions/HeightTransition.vue';
import FloatingLabelInput from '@/components/tailwind/forms/FloatingLabelInput.vue';
import PersonalMessageTemplateSelector from '@/components/ui/envelopes/PersonalMessageTemplateSelector.vue';
import { useActions } from 'vuex-composition-helpers/dist';

export default defineComponent({
  components: {
    FloatingLabelInput,
    HeightTransition,
    ValidationError,
    PersonalMessageTemplateSelector,
    FloatingLabelTextArea,
    BaseButton,
  },
  props: {
    /** @type {import('vue').PropOptions<string[]>} */
    errors: {
      type: Array,
    },
    value: {
      type: String,
      required: true,
    },
  },
  setup(props, { emit }) {
    const templateNameText = ref('');
    const showSaveOptions = ref(false);
    const createMessageErrors = ref(null);

    const { createMessage } = useActions({
      createMessage: 'company/ADD_MESSAGE',
    });

    const templateNameErrorMessage = computed(() => (createMessageErrors.value ? createMessageErrors.value.name[0] : undefined));

    function handleMessageTemplateSelect(event) {
      emit('input', event.text);
    }

    async function handleSaveMessageTemplate() {
      createMessageErrors.value = null;

      try {
        await createMessage({ name: templateNameText.value, text: props.value });

        // Reset the save message template fields
        templateNameText.value = '';
        showSaveOptions.value = false;
      } catch (e) {
        createMessageErrors.value = e.response.data.errors;
      }
    }

    function cancelTemplateSave() {
      templateNameText.value = '';
      showSaveOptions.value = false;
    }

    return {
      templateNameText,
      showSaveOptions,
      handleMessageTemplateSelect,
      handleSaveMessageTemplate,
      cancelTemplateSave,
      createMessageErrors,
      templateNameErrorMessage,
    };
  },
});
</script>
